import { useMemo, useRef, useState } from "react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { usePoll } from "@/hooks/usePoll";
import { usePrefs } from "@/context/PrefsContext";
import { fmtPrice, fmtPct, fmtTime, pnlClass, CATEGORY_LABEL } from "@/lib/format";
import { AssetIcon, DataBadge, MarketStatus, Empty } from "@/components/kit";
import { PriceChart } from "@/components/trade/PriceChart";

const MiniChart = ({ data }) => {
  if (!data || data.length < 2) return <div className="h-14" />;
  const min = Math.min(...data), max = Math.max(...data), span = max - min || 1;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * 100},${38 - ((v - min) / span) * 34}`).join(" ");
  const up = data[data.length - 1] >= data[0];
  const color = up ? "hsl(var(--up))" : "hsl(var(--down))";
  return (
    <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="h-14 w-full" aria-hidden="true">
      <polygon points={`0,40 ${pts} 100,40`} fill={color} opacity=".12" />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.4" vectorEffect="non-scaling-stroke" />
    </svg>
  );
};

const TFS = ["5m", "15m", "1h", "4h", "1d"];

export const ChartDialog = ({ a, onClose }) => {
  const { theme } = usePrefs();
  const [tf, setTf] = useState("1h");
  const [candles, setCandles] = useState([]);
  const [err, setErr] = useState("");
  usePoll(() => api.get(`/market/candles/${a.symbol}?tf=${tf}&limit=300`).then((r) => { setCandles(r.data.candles); setErr(""); }).catch((e) => setErr(errMsg(e, "Gráfico indisponível"))), 30000, [a.symbol, tf]);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-5xl" data-testid="live-chart-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3"><AssetIcon symbol={a.symbol} category={a.category} size={32} />{a.name} <span className="num text-sm text-muted-foreground">{a.symbol}</span><DataBadge type={a.data_type} /></DialogTitle>
          <DialogDescription>Fonte: {a.source || "—"} · gráfico de velas com volume e médias móveis.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-1">{TFS.map((t) => (
          <button key={t} onClick={() => setTf(t)} data-testid={`live-chart-tf-${t}`}
            className={`rounded-md px-3 py-1.5 num text-xs font-semibold ${tf === t ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>{t}</button>))}</div>
        <div className="h-[460px]">
          {err ? <div className="grid h-full place-items-center text-sm text-muted-foreground">{err}</div>
            : <PriceChart candles={candles} chartType="candle" ind={{ ma: true, volume: true }} decimals={a.decimals} theme={theme} fitKey={`${a.symbol}-${tf}`} />}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Card = ({ a, spark, flash, onOpen }) => (
  <button onClick={onOpen} data-testid={`live-card-${a.symbol}`}
    className={`surface p-4 text-left transition-colors hover:border-primary ${flash}`}>
    <div className="flex items-center gap-2">
      <AssetIcon symbol={a.symbol} category={a.category} size={28} />
      <div className="min-w-0 flex-1"><div className="truncate text-sm font-semibold">{a.name}</div><div className="num text-[11px] text-muted-foreground">{a.symbol}</div></div>
      <DataBadge type={a.data_type} testid={`live-badge-${a.symbol}`} />
    </div>
    <MiniChart data={spark} />
    <div className="flex items-end justify-between">
      <span className="num text-lg font-semibold" data-testid={`live-price-${a.symbol}`}>{fmtPrice(a.price, a.decimals)}</span>
      <span className={`num text-xs ${pnlClass(a.change_pct)}`}>{fmtPct(a.change_pct)}</span>
    </div>
    <div className="mt-1 flex justify-between text-[11px] text-muted-foreground"><MarketStatus status={a.market_status} /><span className="num">{fmtTime(a.updated_at)}</span></div>
  </button>
);

export const LiveMarketGrid = () => {
  const [assets, setAssets] = useState([]);
  const [spark, setSpark] = useState({});
  const [cat, setCat] = useState("all");
  const [open, setOpen] = useState(null);
  const prev = useRef({});
  const [prevMap, setPrevMap] = useState({});
  usePoll(() => api.get("/market/assets").then((r) => {
    setPrevMap(prev.current);
    prev.current = Object.fromEntries(r.data.map((a) => [a.symbol, a.price]));
    setAssets(r.data);
  }).catch(() => {}), 3000);
  usePoll(() => api.get("/market/sparklines").then((r) => setSpark(r.data)).catch(() => {}), 300000);
  const rows = useMemo(() => assets.filter((a) => cat === "all" || a.category === cat), [assets, cat]);
  const series = (a) => (spark[a.symbol] && a.price != null ? [...spark[a.symbol], a.price] : spark[a.symbol]);
  const flash = (a) => { const p = prevMap[a.symbol]; return p == null || p === a.price ? "" : a.price > p ? "flash-up" : "flash-down"; };

  return (
    <div className="mb-8" data-testid="live-market-grid">
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="eyebrow flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-up live-dot" />Mercado ao vivo · {assets.length} ativos</div>
        <Tabs value={cat} onValueChange={setCat}>
          <TabsList className="h-auto flex-wrap">{[["all", "Todos"], ...Object.entries(CATEGORY_LABEL)].map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`live-tab-${k}`}>{l}</TabsTrigger>)}</TabsList>
        </Tabs>
      </div>
      {rows.length === 0 ? <Empty text="Carregando cotações…" /> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5 gap-3">
          {rows.map((a) => <Card key={`${a.symbol}-${a.price}`} a={a} spark={series(a)} flash={flash(a)} onOpen={() => setOpen(a)} />)}
        </div>
      )}
      {open && <ChartDialog a={open} onClose={() => setOpen(null)} />}
    </div>
  );
};
